import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Search, 
  Filter, 
  ChevronDown, 
  ExternalLink, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  MoreVertical,
  DollarSign,
  User,
  Phone,
  Mail,
  Zap,
  ArrowRight,
  Filter as FilterIcon,
  X,
  Eye,
  Edit2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const AdminPayments = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    advance_amount: 0,
    final_amount: 0
  });

  const statuses = [
    'PENDIENTE', 'EN COLA', 'LEÍDO', 'COMUNICÁNDONOS', 
    'PAGO ADELANTO', 'TRABAJO EN CURSO', 'TRABAJO FINALIZADO', 'PAGO FINAL'
  ];

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const { data } = await api.orders.getAll();
      setOrders(data);
    } catch (error) {
      toast.error('Error al cargar pedidos.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId) => {
    try {
      await api.orders.update(orderId, editForm);
      toast.success('Pedido actualizado.');
      setIsEditing(false);
      loadOrders();
    } catch (error) {
      toast.error('Error al actualizar.');
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.services?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDIENTE': return 'bg-white/10 text-white/40';
      case 'PAGO ADELANTO': return 'bg-blue-500/10 text-blue-500';
      case 'TRABAJO EN CURSO': return 'bg-yellow-500/10 text-yellow-500';
      case 'PAGO FINAL': return 'bg-purple-500/10 text-purple-500';
      case 'TRABAJO FINALIZADO': return 'bg-[#C6FF00]/10 text-[#C6FF00]';
      default: return 'bg-white/5 text-white/20';
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10 text-white font-sans selection:bg-[#C6FF00] selection:text-black">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
           <div className="flex items-center gap-3 mb-2">
              <div className="w-1 h-6 bg-[#C6FF00] rounded-full shadow-[0_0_15px_rgba(198,255,0,0.4)]"></div>
              <h1 className="text-3xl font-black uppercase tracking-tight italic">Gestión de Pagos</h1>
           </div>
           <p className="text-[10px] font-black uppercase tracking-widest text-white/20 italic">Monitorización de Flujo y Pipeline de Servicios</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={16} />
          <input 
            type="text" 
            placeholder="Buscar por cliente o servicio..." 
            className="w-full bg-white/[0.02] border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#C6FF00]/20 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="relative">
          <FilterIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={16} />
          <select 
            className="w-full bg-white/[0.02] border border-white/5 rounded-2xl py-4 pl-12 pr-10 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#C6FF00]/20 transition-all appearance-none cursor-pointer"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">TODOS LOS ESTADOS</option>
            {statuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.02] border border-white/5 rounded-[2.5rem] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5 bg-white/[0.02]">
                <th className="px-8 py-5 text-[8px] font-black uppercase tracking-[0.4em] text-white/20 italic">Cliente</th>
                <th className="px-8 py-5 text-[8px] font-black uppercase tracking-[0.4em] text-white/20 italic">Servicio</th>
                <th className="px-8 py-5 text-[8px] font-black uppercase tracking-[0.4em] text-white/20 italic">Estado</th>
                <th className="px-8 py-5 text-[8px] font-black uppercase tracking-[0.4em] text-white/20 italic">Montos</th>
                <th className="px-8 py-5 text-[8px] font-black uppercase tracking-[0.4em] text-white/20 italic">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredOrders.map(order => (
                <tr key={order.id} className="group hover:bg-white/[0.01] transition-colors">
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                       <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/20 transition-all">
                          <User size={16} className="text-white/40" />
                       </div>
                       <div>
                          <p className="text-[10px] font-bold uppercase text-white mb-0.5">{order.full_name}</p>
                          <div className="flex items-center gap-3">
                             <span className="flex items-center gap-1 text-[8px] font-black text-white/20 tracking-tighter italic"><Phone size={8} /> {order.phone}</span>
                          </div>
                       </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                     <p className="text-[10px] font-black uppercase italic text-white/60 tracking-widest">{order.services?.title || 'Unknown_Service'}</p>
                  </td>
                  <td className="px-8 py-6">
                     <span className={`px-4 py-2 rounded-full text-[7px] font-black tracking-[0.2em] italic uppercase ${getStatusColor(order.status)}`}>
                        {order.status}
                     </span>
                  </td>
                  <td className="px-8 py-6">
                     <div className="space-y-1">
                        <p className="text-[8px] font-black italic tracking-widest text-[#C6FF00]/40 uppercase">Advance: ${order.advance_amount}</p>
                        <p className="text-[8px] font-black italic tracking-widest text-white/20 uppercase">Final: ${order.final_amount}</p>
                     </div>
                  </td>
                  <td className="px-8 py-6">
                    <button 
                      onClick={() => {
                        setSelectedOrder(order);
                        setEditForm({
                          status: order.status,
                          advance_amount: order.advance_amount,
                          final_amount: order.final_amount
                        });
                        setIsEditing(true);
                      }}
                      className="p-3 bg-white/5 rounded-xl border border-white/10 hover:border-[#C6FF00]/40 transition-all text-[#C6FF00]/40 hover:text-[#C6FF00]"
                    >
                       <Edit2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {isEditing && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
             <motion.div 
               initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
               onClick={() => setIsEditing(false)}
               className="absolute inset-0 bg-black/90 backdrop-blur-xl" 
             />
             <motion.div
               initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }}
               className="w-full max-w-md bg-[#0A0A0A] border border-white/10 p-8 sm:p-10 rounded-[2.5rem] relative z-10 shadow-2xl overflow-y-auto max-h-[90vh]"
             >
                <div className="flex items-center justify-between mb-10">
                   <div>
                      <span className="text-[#C6FF00] text-[8px] font-black uppercase tracking-[0.4em] mb-1 block italic leading-none">Pipeline_Control</span>
                      <h2 className="text-xl font-black uppercase italic tracking-tight">Gestionar Pedido</h2>
                   </div>
                   <button onClick={() => setIsEditing(false)} className="p-2 text-white/20 hover:text-white transition-all"><X size={20} /></button>
                </div>

                <div className="space-y-6">
                   <div className="space-y-2">
                      <label className="text-[8px] font-black tracking-widest text-white/20 italic uppercase ml-1">Estado de la Fase</label>
                      <select 
                        className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-[10px] font-black uppercase tracking-widest outline-none focus:border-[#C6FF00]/40 transition-all"
                        value={editForm.status}
                        onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                      >
                         {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                   </div>

                   <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-[8px] font-black tracking-widest text-white/20 italic uppercase ml-1">Monto Adelanto (USD)</label>
                        <input 
                          type="number" 
                          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-[10px] font-black outline-none focus:border-[#C6FF00]/40 transition-all"
                          value={editForm.advance_amount}
                          onChange={(e) => setEditForm({...editForm, advance_amount: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[8px] font-black tracking-widest text-white/20 italic uppercase ml-1">Monto Final (USD)</label>
                        <input 
                          type="number" 
                          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-[10px] font-black outline-none focus:border-[#C6FF00]/40 transition-all"
                          value={editForm.final_amount}
                          onChange={(e) => setEditForm({...editForm, final_amount: e.target.value})}
                        />
                      </div>
                   </div>

                   {/* Receipts Review */}
                   <div className="space-y-4 pt-6">
                      <h4 className="text-[8px] font-black uppercase italic tracking-[0.2em] text-[#C6FF00]/40">Comprobantes de Pago</h4>
                      <div className="grid grid-cols-2 gap-4">
                         <div className="space-y-2">
                            <p className="text-[7px] font-black uppercase text-white/20">Recibo Adelanto</p>
                            {selectedOrder?.advance_receipt_url ? (
                              <a href={selectedOrder.advance_receipt_url} target="_blank" rel="noreferrer" className="block aspect-[16/10] bg-white/5 rounded-xl border border-white/10 overflow-hidden group relative">
                                 <img src={selectedOrder.advance_receipt_url} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                 <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"><Eye size={18} /></div>
                              </a>
                            ) : (
                              <div className="aspect-[16/10] bg-white/[0.01] border border-white/5 border-dashed rounded-xl flex items-center justify-center text-[10px] text-white/10 italic">No Upload</div>
                            )}
                         </div>
                         <div className="space-y-2">
                            <p className="text-[7px] font-black uppercase text-white/20">Recibo Final</p>
                            {selectedOrder?.final_receipt_url ? (
                              <a href={selectedOrder.final_receipt_url} target="_blank" rel="noreferrer" className="block aspect-[16/10] bg-white/5 rounded-xl border border-white/10 overflow-hidden group relative">
                                 <img src={selectedOrder.final_receipt_url} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform" />
                                 <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"><Eye size={18} /></div>
                              </a>
                            ) : (
                              <div className="aspect-[16/10] bg-white/[0.01] border border-white/5 border-dashed rounded-xl flex items-center justify-center text-[10px] text-white/10 italic">No Upload</div>
                            )}
                         </div>
                      </div>
                   </div>

                   <div className="pt-10">
                      <button 
                        onClick={() => handleUpdateStatus(selectedOrder.id)}
                        className="w-full py-5 bg-[#C6FF00] text-black rounded-xl font-black uppercase tracking-[0.2em] text-[10px] shadow-lg hover:scale-105 active:scale-95 transition-all"
                      >
                         Actualizar Protocolo
                      </button>
                   </div>
                </div>
             </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
